/**
 * Content loader utilities for learning materials.
 *
 * Handles loading and parsing of grammar lessons, quizzes, and common sentences
 * (Redemittel) from markdown files. Provides functions to load content by level
 * and topic, extract metadata, and transform markdown to structured data.
 *
 * @module contentLoader
 */

import { apiGet } from './apiUtils';
import { DEFAULT_LEVEL } from './constants';
import { logger } from './logger';

// ============================================================================
// Type Definitions
// ============================================================================

export interface QuizBlank {
  placeholder: number;
  acceptedAnswers: string[];
  caseSensitive: boolean;
  normalize: boolean;
  explanation?: string;
}

export interface QuizQuestion {
  id: string;
  text: string;
  blanks: QuizBlank[];
}

export interface QuizSection {
  id: string;
  title: string;
  questions: QuizQuestion[];
}

export interface GrammarQuiz {
  id: string;
  lang?: string;
  title: string;
  type: string;
  instructions: string;
  questions?: QuizQuestion[];
  sections?: QuizSection[];
  answerKey: Record<string, string[]>;
}

interface GrammarContent {
  title: string;
  content: string;
  level: string;
  topic: string;
  quiz?: GrammarQuiz;
}

interface ContentMetadata {
  title?: string;
  level?: string;
  description?: string;
}

// ============================================================================
// Metadata Extraction
// ============================================================================

/**
 * Extracts metadata from markdown content
 * Looks for frontmatter-style metadata or derives from filename/content
 */
function extractMetadata(content: string, filename: string): ContentMetadata {
  // Extract title from first h1 if present
  const titleRegex = /^#\s+(.+)$/m;
  const titleMatch = titleRegex.exec(content);
  const title = titleMatch
    ? titleMatch[1]
    : filename.replace(/\.md$/, "").replaceAll('-', ' ');

  // Extract level from filename (e.g., A1, A2, etc.)
  const levelRegex = /[A-C][1-2]/;
  const levelMatch = levelRegex.exec(filename);
  const level = levelMatch ? levelMatch[0] : DEFAULT_LEVEL;

  return {
    title,
    level,
  };
}

// ============================================================================
// Quiz Loading
// ============================================================================

/**
 * Loads quiz data for a grammar topic
 */
export async function loadGrammarQuiz(
  level: string,
  topic: string,
): Promise<GrammarQuiz | null> {
  return await apiGet<GrammarQuiz>(
    `/contents/grammar/${level.toUpperCase()}/${topic}.quiz.json`,
    {
      skipApiBase: true,
      requireAuth: false,
      errorReturnValue: null,
      errorMessage: `Failed to load grammar quiz: ${topic}`,
      context: 'loadGrammarQuiz',
    },
  );
}

// ============================================================================
// Content Loading & Transformation
// ============================================================================

/**
 * Loads markdown content from the contents directory
 */
export async function loadGrammarContent(
  level: string,
  topic: string,
): Promise<GrammarContent | null> {
  try {
    const url = `/contents/grammar/${level.toUpperCase()}/${topic}.md`;
    logger.debug('Loading grammar content', { url, level, topic });
    
    const response = await fetch(url);

    if (!response.ok) {
      logger.warn('Content not found', { url, status: response.status });
      return null;
    }

    // Check if we got HTML instead of markdown (common when file doesn't exist)
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      logger.error('Received HTML instead of markdown. File may not exist.', undefined, { url, contentType });
      return null;
    }

    const content = await response.text();
    
    // Additional check: if content looks like HTML, it's probably the index page
    if (content.trim().startsWith('<!DOCTYPE') || content.trim().startsWith('<!doctype')) {
      logger.error('Received HTML page instead of markdown. File may not exist.', undefined, { url });
      return null;
    }
    
    const metadata = extractMetadata(content, `${topic}.md`);

    // Try to load quiz data if available
    const quiz = await loadGrammarQuiz(level, topic);

    return {
      title: metadata.title || topic,
      content,
      level: metadata.level || level,
      topic,
      quiz,
    };
  } catch (error) {
    logger.error('Error loading grammar content', error, { level, topic });
    return null;
  }
}

// ============================================================================
// Topic Utilities
// ============================================================================

/**
 * Gets the URL-friendly slug for a grammar topic
 */
export function getTopicSlug(title: string): string {
  return title
    .toLowerCase()
    .replaceAll(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
    .replaceAll(/\s+/g, "-") // Replace spaces with hyphens
    .replaceAll(/--+/g, "-") // Replace multiple hyphens with single hyphen
    .trim();
}

/**
 * Maps display titles to their corresponding filename slugs
 */
export const topicMappings: Record<string, string> = {
  Personalpronomen: "personalpronomen-nominativ",
  "Konjugation Präsens": "konjugation-praesens",
  "Besondere Verben": "besondere-verben",
  "Verben mit Vokalwechsel": "verben-vokalwechsel",
  Modalverben: "modal-verben",
  "Modalverben Gebrauch": "modal-verben-gebrauch",
  "Modalverben Gebrauch 2": "modal-verben-gebrauch-2",
  "Trennbare Verben": "trennbare-verben",
  Imperativ: "imperative",
  "W-Fragen": "w-fragen",
  "Ja-/Nein-Fragen": "ja-nein-fragen",
  Wortstellung: "wortstellung",
  "Nomen Plural": "plural-nomen",
  Artikel: "artikel",
  Negation: "negation",
  Akkusativ: "akkusativ",
  Dativ: "dativ",
  Possessivartikel: "possessivartikel",
  "Demonstrativ- und Interrogativpronomen": "demonstrativ-interrogativpronomen",
  "Personalpronomen Akkusativ und Dativ": "personalpronomen-akkusativ-dativ",
  "Präteritum sein und haben": "praeteritum-sein-haben",
  "Perfekt mit haben": "perfekt-mit-haben",
  Zeitpräpositionen: "temporal-prepositions",
  "Präpositionen mit Dativ": "dative-prepositions",
  "Präpositionen mit Akkusativ": "accusative-prepositions",
  "Wechselpräpositionen mit Dativ": "two-way-prepositions-dative",
  Konjunktionen: "coordinating-conjunctions",
  Komposita: "compound-nouns",
  "Zusammengesetzte Verben": "compound-verbs",
  "Artikel und Nomen Genus": "articles-noun-genders",
  "Numbers 1-100": "numbers",
};

/**
 * Gets the filename for a topic title
 */
export function getTopicFilename(title: string): string {
  return topicMappings[title] || getTopicSlug(title);
}

// ============================================================================
// Redemittel (Common Sentences) Loading & Parsing
// ============================================================================

/**
 * Interface for Redemittel (common sentences) category
 */
export interface RedemittelCategory {
  category: string;
  sentences: string[];
  subsections?: RedemittelSubsection[];
}

/**
 * Interface for subsections within a category
 */
export interface RedemittelSubsection {
  title: string;
  sentences: string[];
}

/**
 * Loads and parses Redemittel markdown content
 * Parses structure: ## Category followed by - sentence items
 */
export async function loadRedemittelContent(
  level: string,
): Promise<RedemittelCategory[] | null> {
  try {
    const response = await fetch(
      `/contents/redemittel/${level.toUpperCase()}_Redemittel_Katalog.md`,
    );

    if (!response.ok) {
      logger.warn('Redemittel content not found', { 
        url: `/contents/redemittel/${level.toUpperCase()}_Redemittel_Katalog.md`,
        status: response.status 
      });
      return null;
    }

    const content = await response.text();
    const categories: RedemittelCategory[] = [];

    // Split content by ## headings (categories)
    const sections = content.split(/^##\s+/m).filter((s) => s.trim());

    sections.forEach((section) => {
      const lines = section.split("\n");
      const categoryName = lines[0].trim();

      // Parse subsections and sentences
      const subsections: RedemittelSubsection[] = [];
      const generalSentences: string[] = [];

      let currentSubsection: RedemittelSubsection | null = null;

      lines.slice(1).forEach((line) => {
        const trimmedLine = line.trim();

        // Check if this is a subsection title (bold text)
        const boldRegex = /^- \*\*(.*?)\*\*:?\s*$/;
        const boldMatch = boldRegex.exec(trimmedLine);
        if (boldMatch) {
          // Save previous subsection if exists
          if (currentSubsection && currentSubsection.sentences.length > 0) {
            subsections.push(currentSubsection);
          }

          // Start new subsection
          currentSubsection = {
            title: boldMatch[1],
            sentences: [],
          };
          return;
        }

        // Check if this is a sentence (starts with -)
        const boldCheckRegex = /^- \*\*.*\*\*:?\s*$/;
        if (
          trimmedLine.startsWith("-") &&
          !boldCheckRegex.exec(trimmedLine)
        ) {
          const sentence = trimmedLine.substring(1).trim();
          if (sentence.length > 0) {
            if (currentSubsection) {
              currentSubsection.sentences.push(sentence);
            } else {
              generalSentences.push(sentence);
            }
          }
        }
      });

      // Add the last subsection if exists
      if (currentSubsection && currentSubsection.sentences.length > 0) {
        subsections.push(currentSubsection);
      }

      if (
        categoryName &&
        (generalSentences.length > 0 || subsections.length > 0)
      ) {
        categories.push({
          category: categoryName,
          sentences: generalSentences,
          subsections: subsections.length > 0 ? subsections : undefined,
        });
      }
    });

    return categories.length > 0 ? categories : null;
  } catch (error) {
    logger.error('Error loading Redemittel content', error, { level });
    return null;
  }
}

